import { useCallback, useEffect, useRef, useState } from 'react';
import { HashRouter } from 'react-router-dom';
import {
  DEFAULT_LANGUAGE,
  DEFAULT_THEME_ID,
  DEFAULT_THEME_MODE,
  isLanguage,
  type Language,
} from '@astral/shared';
import { getUnsafeUser, onMiniAppActivated, onMiniAppDeactivated } from '@astral/telegram/webapp';
import { AuthProvider, useAuth } from './providers/AuthProvider.js';
import { I18nProvider } from './providers/I18nProvider.js';
import { ThemeProvider } from './providers/ThemeProvider.js';
import { ToastProvider } from './providers/ToastProvider.js';
import { readCachedPrefs } from './lib/prefs.js';
import { useReducedMotion } from './lib/useReducedMotion.js';
import { useStableViewport } from './lib/useStableViewport.js';
import { ShootingStars } from './components/ShootingStars.js';
import { Stardust } from './components/Stardust.js';
import { SplashScreen } from './screens/status/SplashScreen.js';
import { ErrorScreen } from './screens/status/ErrorScreen.js';
import { NotInTelegramScreen } from './screens/status/NotInTelegramScreen.js';
import { OnboardingScreen } from './screens/OnboardingScreen.js';
import { MainRouter } from './router/MainRouter.js';

/** Best-effort language guess before the user's saved preference is known. */
function guessLanguage(): Language {
  const code = getUnsafeUser()?.language_code?.split('-')[0];
  return code && isLanguage(code) ? code : DEFAULT_LANGUAGE;
}

/**
 * Reads auth state and picks the active language + theme (from the user's saved
 * preferences, falling back to defaults), then renders the appropriate screen.
 */
/** Full-motion entry choreography; reduced motion collapses to a short fade. */
const ENTRY_MS = 440;
const ENTRY_REDUCED_MS = 200;

function AppFlow(): JSX.Element {
  const { status, user } = useAuth();
  const reduced = useReducedMotion();
  // Keeps the celestial background stable while the keyboard is open.
  useStableViewport();

  // Before login resolves, fall back to the last cached preferences (instant,
  // flicker-free paint), then to a language guess / defaults.
  const cached = readCachedPrefs();
  const language = user?.language ?? cached.language ?? guessLanguage();

  /*
   * Astral ships ONE official visual identity. The theme is pinned here rather
   * than read from preferences, so a legacy `themeId` / `themeMode` saved before
   * the picker was removed can never resurrect an old look. The stored fields
   * are left untouched for backend/schema compatibility — they simply no longer
   * drive rendering. Every other preference (language, onboarding, …) is
   * unaffected.
   */
  const themeId = DEFAULT_THEME_ID;
  const themeMode = DEFAULT_THEME_MODE;

  /*
   * Controlled entry → Home transition. Both layers stay mounted for its whole
   * duration so there is never a blank frame or route flash; the entry layer is
   * lifted out of flow and translates up while Home rises into place beneath
   * it. This wraps the existing navigation — it does not replace it.
   */
  const [entering, setEntering] = useState(false);
  const enteringRef = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  /*
   * Welcome is the Mini App's ENTRY SCREEN, not one-time account onboarding.
   *
   * It used to be gated on the persisted `onboarded` flag, which meant that
   * once a user had entered, Welcome never came back unless the bot's /start
   * reset the flag — hence "I have to send /start to see it again". That flag
   * still exists for the bot's own conversation flow, but the Mini App no
   * longer reads or writes it.
   *
   * `entered` is per-SESSION state: a fresh launch always starts false, so
   * every Open App begins at Welcome regardless of projects, Premium, bot
   * language or account age. No user data is touched.
   */
  const [entered, setEntered] = useState(false);

  useEffect(() => () => clearTimeout(timer.current), []);

  const beginEntry = useCallback((): void => {
    if (enteringRef.current) return; // hard guard against double activation
    enteringRef.current = true;
    setEntering(true);
    timer.current = setTimeout(() => {
      enteringRef.current = false;
      setEntering(false);
      setEntered(true);
      window.scrollTo(0, 0);
    }, reduced ? ENTRY_REDUCED_MS : ENTRY_MS);
  }, [reduced]);

  /*
   * Returning to the Mini App after minimising it starts at Welcome again.
   *
   * This listens to Telegram's `activated` / `deactivated` lifecycle events
   * (Bot API 8.0+), NOT to DOM `focus` / `visibilitychange` — those also fire
   * for the software keyboard and every incidental blur, which would make
   * Welcome reappear mid-use. Resetting only when a real `deactivated` was
   * seen first means an ordinary keyboard open can never trigger it, and a
   * spurious `activated` at launch is ignored.
   *
   * Only the entry screen resets; projects, caches and state are untouched.
   */
  const wasDeactivated = useRef(false);
  useEffect(() => {
    const offDeactivated = onMiniAppDeactivated(() => {
      wasDeactivated.current = true;
    });
    const offActivated = onMiniAppActivated(() => {
      if (!wasDeactivated.current) return; // never deactivated → not a real return
      wasDeactivated.current = false;
      if (enteringRef.current) return; // mid-transition: leave it alone
      setEntered(false);
      window.scrollTo(0, 0);
    });
    return () => {
      offDeactivated();
      offActivated();
    };
  }, []);

  const showEntry = !entered || entering;
  const showHome = entered || entering;

  return (
    <I18nProvider language={language}>
      <LanguageDirection language={language} />
      <ThemeProvider themeId={themeId} mode={themeMode}>
        <ToastProvider>
          <div className="app-root">
            <Stardust />
            <ShootingStars />
            {status === 'loading' && <SplashScreen />}
            {status === 'not-in-telegram' && <NotInTelegramScreen />}
            {status === 'error' && <ErrorScreen />}
            {status === 'authenticated' && user && (
              <>
                {showHome && (
                  <div className={`screen-layer${entering ? ' screen-layer--arriving' : ''}`}>
                    <HashRouter>
                      <MainRouter />
                    </HashRouter>
                  </div>
                )}
                {showEntry && (
                  <div className={`screen-layer${entering ? ' screen-layer--leaving' : ''}`}>
                    <OnboardingScreen onEnter={beginEntry} entering={entering} />
                  </div>
                )}
              </>
            )}
          </div>
        </ToastProvider>
      </ThemeProvider>
    </I18nProvider>
  );
}

/** Side-effect component: keeps <html dir/lang> in sync with the language. */
function LanguageDirection({ language }: { language: Language }): null {
  const dir = language === 'fa' ? 'rtl' : 'ltr';
  document.documentElement.setAttribute('dir', dir);
  document.documentElement.setAttribute('lang', language);
  return null;
}

export function App(): JSX.Element {
  return (
    <AuthProvider>
      <AppFlow />
    </AuthProvider>
  );
}
